import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';
import CommunicationService from '../../../services/communications';

const EmailCampaignPanel = () => {
  const [subject, setSubject] = useState('');
  const [emailContent, setEmailContent] = useState('');
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [selectedContacts, setSelectedContacts] = useState([]);
  const [scheduleDate, setScheduleDate] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Data states
  const [templates, setTemplates] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [messages, setMessages] = useState([]);
  const [loadingData, setLoadingData] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoadingData(true);
    try {
      const [templatesRes, contactsRes, campaignsRes, messagesRes] = await Promise.all([
        CommunicationService?.getTemplates('email'),
        CommunicationService?.getContacts(),
        CommunicationService?.getCampaigns(),
        CommunicationService?.getMessages({ type: 'email', limit: 10 })
      ]);

      if (templatesRes?.success) {
        setTemplates(templatesRes?.data || []);
      }
      if (contactsRes?.success) {
        setContacts(contactsRes?.data || []);
      }
      if (campaignsRes?.success) {
        setCampaigns(campaignsRes?.data?.filter(c => c?.type === 'email') || []);
      }
      if (messagesRes?.success) {
        setMessages(messagesRes?.data || []);
      }
    } catch (error) {
      console.error('Error loading data:', error);
      setError('Failed to load data');
    } finally {
      setLoadingData(false);
    }
  };

  const handleSendEmail = async () => {
    if (!subject || !emailContent || selectedContacts?.length === 0) {
      setError('Please enter subject, content and select at least one contact');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      let successCount = 0;
      const errors = [];

      for (const contactId of selectedContacts) {
        const contact = contacts?.find(c => c?.id === contactId);
        if (!contact?.email) {
          errors?.push(`${contact?.name || 'Unknown'}: No email address`);
          continue;
        }

        // Create message record
        const messageRes = await CommunicationService?.createMessage({
          type: 'email',
          direction: 'outbound',
          contact_id: contactId,
          template_id: selectedTemplate || null,
          subject: subject,
          content: emailContent,
          to_address: contact?.email,
          from_address: 'noreply@leadgenpro.com',
          status: 'pending'
        });

        if (messageRes?.success) {
          // Send email
          const sendRes = await CommunicationService?.sendEmail({
            to: contact?.email,
            subject: subject,
            html: emailContent?.replace(/\n/g, '<br>'),
            fromName: 'LeadGen Pro',
            messageId: messageRes?.data?.id
          });

          if (sendRes?.success) {
            successCount++;
            // Track usage
            await CommunicationService?.trackUsage('emails', 1);
          } else {
            errors?.push(`${contact?.name || 'Unknown'}: ${sendRes?.error}`);
          }
        } else {
          errors?.push(`${contact?.name || 'Unknown'}: ${messageRes?.error}`);
        }
      }

      if (successCount > 0) {
        setSuccess(`Successfully sent ${successCount} email${successCount > 1 ? 's' : ''}`);
        setSubject('');
        setEmailContent('');
        setSelectedContacts([]);
        loadData(); // Refresh data
      }

      if (errors?.length > 0) {
        setError(`Errors: ${errors?.join(', ')}`);
      }
    } catch (error) {
      console.error('Error sending emails:', error);
      setError('Failed to send emails');
    } finally {
      setLoading(false);
    }
  };

  const handleContactSelect = (contactId, checked) => {
    if (checked) {
      setSelectedContacts([...selectedContacts, contactId]);
    } else {
      setSelectedContacts(selectedContacts?.filter(id => id !== contactId));
    }
  };

  const handleTemplateSelect = (templateId) => {
    setSelectedTemplate(templateId);
    const template = templates?.find(t => t?.id === templateId);
    if (template) {
      if (template?.subject) setSubject(template?.subject);
      if (template?.content) setEmailContent(template?.content);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'delivered':
        return 'text-success';
      case 'pending':
        return 'text-warning';
      case 'failed':
        return 'text-destructive';
      case 'sent':
        return 'text-primary';
      case 'opened':
        return 'text-secondary';
      default:
        return 'text-muted-foreground';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'delivered':
        return 'Check';
      case 'pending':
        return 'Clock';
      case 'failed':
        return 'X';
      case 'sent':
        return 'Send';
      case 'opened':
        return 'Eye';
      default:
        return 'Mail';
    }
  };

  const templateOptions = templates?.map(template => ({
    value: template?.id,
    label: template?.name
  })) || [];

  if (loadingData) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <Icon name="Loader2" size={24} className="animate-spin mx-auto mb-2 text-primary" />
          <p className="text-muted-foreground">Loading email data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {error && (
        <div className="bg-destructive/10 border border-destructive/20 text-destructive px-4 py-3 rounded-lg">
          {error}
        </div>
      )}
      {success && (
        <div className="bg-success/10 border border-success/20 text-success px-4 py-3 rounded-lg">
          {success}
        </div>
      )}
      {/* Email Composer */}
      <div className="bg-card border border-border rounded-lg p-6 shadow-elevation-1">
        <h3 className="text-lg font-semibold text-foreground mb-4">Compose Email</h3>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="space-y-4">
            <Select
              label="Email Template"
              placeholder="Select template (optional)"
              options={templateOptions}
              value={selectedTemplate}
              onChange={handleTemplateSelect}
            />
            <Input
              label="Subject"
              value={subject}
              onChange={(e) => setSubject(e?.target?.value)}
              placeholder="Enter email subject"
            />
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Email Content
              </label>
              <textarea
                value={emailContent}
                onChange={(e) => setEmailContent(e?.target?.value)}
                placeholder="Type your email content here..."
                className="w-full h-40 px-3 py-2 border border-border rounded-md bg-input text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent resize-none"
              />
            </div>
            <Input
              label="Schedule Send (Optional)"
              type="datetime-local"
              value={scheduleDate}
              onChange={(e) => setScheduleDate(e?.target?.value)}
            />
          </div>
          
          <div>
            <h4 className="text-sm font-medium text-foreground mb-3">Select Recipients</h4>
            <div className="border border-border rounded-lg max-h-64 overflow-y-auto">
              {contacts?.length === 0 ? (
                <div className="p-4 text-center text-muted-foreground">
                  No contacts available. Add contacts first.
                </div>
              ) : (
                contacts?.map((contact) => (
                  <div
                    key={contact?.id}
                    className="p-3 border-b border-border last:border-b-0 hover:bg-muted transition-colors duration-200"
                  >
                    <div className="flex items-center space-x-3">
                      <Checkbox
                        checked={selectedContacts?.includes(contact?.id)}
                        onChange={(e) => handleContactSelect(contact?.id, e?.target?.checked)}
                      />
                      <div className="flex-1">
                        <p className="text-sm font-medium text-foreground">{contact?.name}</p>
                        <p className="text-xs text-muted-foreground">{contact?.email}</p>
                        <p className="text-xs text-muted-foreground">{contact?.company}</p>
                      </div>
                      <span className={`text-xs px-2 py-1 rounded-full ${
                        contact?.is_active ?'bg-success/10 text-success' :'bg-muted text-muted-foreground'
                      }`}>
                        {contact?.is_active ? 'active' : 'inactive'}
                      </span>
                    </div>
                  </div>
                ))
              )}
            </div>
            <div className="mt-4 flex space-x-2">
              <Button
                variant="default"
                iconName="Send"
                iconPosition="left"
                disabled={!subject || !emailContent || selectedContacts?.length === 0 || loading}
                onClick={handleSendEmail}
                className="flex-1"
              >
                {loading ? 'Sending...' : `Send Email (${selectedContacts?.length})`}
              </Button>
              <Button 
                variant="outline" 
                iconName="Clock" 
                iconSize={16}
                disabled={!scheduleDate}
              >
                Schedule
              </Button>
            </div>
          </div>
        </div>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Email Campaigns */}
        <div className="bg-card border border-border rounded-lg shadow-elevation-1">
          <div className="p-4 border-b border-border">
            <h3 className="text-lg font-semibold text-foreground">Email Campaigns</h3>
          </div>
          <div className="p-4 space-y-4">
            {campaigns?.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                No email campaigns yet
              </div>
            ) : (
              campaigns?.map((campaign) => (
                <div
                  key={campaign?.id}
                  className="p-4 border border-border rounded-lg hover:bg-muted transition-colors duration-200"
                >
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="font-medium text-foreground">{campaign?.name}</h4>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      campaign?.status === 'sent' ?'bg-success/10 text-success' :'bg-primary/10 text-primary'
                    }`}>
                      {campaign?.status}
                    </span>
                  </div>
                  
                  <div className="grid grid-cols-3 gap-4 mb-3">
                    <div className="text-center">
                      <div className="text-lg font-semibold text-foreground">{campaign?.sent_count || 0}</div>
                      <div className="text-xs text-muted-foreground">Sent</div>
                    </div>
                    <div className="text-center">
                      <div className="text-lg font-semibold text-success">{campaign?.delivered_count || 0}</div>
                      <div className="text-xs text-muted-foreground">Delivered</div>
                    </div>
                    <div className="text-center">
                      <div className="text-lg font-semibold text-secondary">{campaign?.failed_count || 0}</div>
                      <div className="text-xs text-muted-foreground">Failed</div>
                    </div>
                  </div>
                  
                  <div className="flex items-center justify-between text-xs text-muted-foreground mb-3">
                    <span>Total: {campaign?.total_contacts || 0}</span>
                    <span>Delivery Rate: {campaign?.sent_count ? Math.round((campaign?.delivered_count / campaign?.sent_count) * 100) : 0}%</span>
                  </div>
                  
                  <div className="flex items-center justify-between pt-3 border-t border-border">
                    <span className="text-xs text-muted-foreground">
                      Created: {campaign?.created_at ? new Date(campaign.created_at)?.toLocaleDateString() : 'Unknown'}
                    </span>
                    <Button variant="outline" size="sm" iconName="BarChart3" iconSize={14}>
                      Analytics
                    </Button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Message History */}
        <div className="bg-card border border-border rounded-lg shadow-elevation-1">
          <div className="p-4 border-b border-border">
            <h3 className="text-lg font-semibold text-foreground">Recent Emails</h3>
          </div>
          <div className="max-h-96 overflow-y-auto">
            {messages?.length === 0 ? (
              <div className="p-4 text-center text-muted-foreground">
                No emails yet
              </div>
            ) : (
              messages?.map((message) => (
                <div
                  key={message?.id}
                  className={`p-4 border-b border-border last:border-b-0 ${
                    message?.direction === 'inbound' ? 'bg-primary/5' : ''
                  }`}
                >
                  <div className="flex items-start space-x-3">
                    <Icon
                      name={getStatusIcon(message?.status)}
                      size={16}
                      className={getStatusColor(message?.status)}
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-sm font-medium text-foreground">
                          {message?.contact?.name || 'Unknown Contact'}
                        </span>
                        <span className="text-xs text-muted-foreground">
                          {message?.created_at ? new Date(message.created_at)?.toLocaleString() : 'Unknown'}
                        </span>
                      </div>
                      <p className="text-sm font-medium text-foreground mb-1">
                        {message?.subject}
                      </p>
                      <p className="text-sm text-muted-foreground mb-2 line-clamp-2">
                        {message?.content}
                      </p>
                      <div className="flex items-center space-x-2">
                        <span className={`text-xs px-2 py-1 rounded-full ${
                          message?.direction === 'inbound' ?'bg-primary/10 text-primary' :'bg-secondary/10 text-secondary'
                        }`}>
                          {message?.direction}
                        </span>
                        <span className={`text-xs ${getStatusColor(message?.status)}`}>
                          {message?.status}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmailCampaignPanel;